import React, { useState } from "react";
import PropTypes from "prop-types";
import privateAxios from "../../utils/axiosPrivate";
import { createPartnerCustomerURL } from "../../utils/constants/apiEndPoints";
import { FT } from "./partnerPortalShared";

export default function AddCustomerModal({
  signupUrl,
  copied,
  onCopy,
  onClose,
  onSuccess
}) {
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    phone: "",
    notes: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);

  const canSubmit =
    form.name.trim() && form.contact.trim() && form.email.trim() && !submitting;

  const update = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  async function submit(mode) {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Split the single "Primary contact" field into first/last for the
      // existing backend API. TODO accept a single name field on the backend.
      const parts = form.contact.trim().split(/\s+/);
      const firstName = parts[0] || form.contact.trim();
      const lastName = parts.slice(1).join(" ") || ".";

      // mode=link → customer gets the set-password email.
      // mode=preassess → customer email suppressed; info@fundtap.co gets an internal alert.
      const payload = {
        firstName,
        lastName,
        email: form.email.trim(),
        businessName: form.name.trim(),
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        mode
      };

      await privateAxios.post(createPartnerCustomerURL, payload);
      setSubmitted(mode);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          "Couldn't add this client. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  const labelStyle = {
    display: "block",
    fontSize: 11.5,
    color: FT.navy60,
    fontWeight: 700,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 6
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${FT.navy15}`,
    fontSize: 13.5,
    color: FT.navy,
    fontFamily: "inherit",
    outline: "none",
    background: "#fff",
    boxSizing: "border-box"
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(10,31,61,0.36)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 40,
        fontFamily: "DM Sans, system-ui, sans-serif"
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: 560,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 32px 80px rgba(10,31,61,0.4)"
        }}
      >
        {submitted ? (
          <div style={{ padding: "40px 36px 36px", textAlign: "center" }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 99,
                background: FT.mintBg,
                color: FT.mintDeep,
                display: "grid",
                placeItems: "center",
                margin: "0 auto 18px"
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: FT.navy,
                letterSpacing: -0.4
              }}
            >
              {submitted === "link"
                ? "Sign-up link sent"
                : "Sent for pre-assessment"}
            </div>
            <div
              style={{
                fontSize: 14,
                color: FT.navy60,
                margin: "6px auto 0",
                maxWidth: 380
              }}
            >
              {submitted === "link" ? (
                <>
                  We&apos;ve emailed{" "}
                  <b style={{ color: FT.navy }}>{form.email}</b> with their
                  sign-up link. {form.name} will show up under{" "}
                  <b style={{ color: FT.navy }}>Pending</b> in your client book.
                </>
              ) : (
                <>
                  FundTap has received {form.name}&apos;s details. They&apos;ll
                  show up under <b style={{ color: FT.navy }}>Pending</b> in
                  your client book — a FundTap team member will move them into{" "}
                  <b style={{ color: FT.navy }}>Customer DD</b> once they begin
                  the pre-assessment review.
                </>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                marginTop: 22,
                padding: "11px 22px",
                background: FT.navy,
                color: "#fff",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                fontSize: 13.5,
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit("link");
            }}
          >
            <div
              style={{
                padding: "22px 26px 16px",
                borderBottom: `1px solid ${FT.navy08}`
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start"
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 21,
                      fontWeight: 800,
                      color: FT.navy,
                      letterSpacing: -0.4
                    }}
                  >
                    Add a client
                  </div>
                  <div style={{ fontSize: 13, color: FT.navy60, marginTop: 4 }}>
                    We&apos;ll send them a sign-up link. You&apos;ll earn from
                    their first funded invoice.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    border: "none",
                    background: FT.navy04,
                    width: 30,
                    height: 30,
                    borderRadius: 999,
                    cursor: "pointer",
                    color: FT.navy,
                    fontSize: 16,
                    fontFamily: "inherit"
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            {/* Share-link shortcut */}
            <div
              style={{
                padding: "14px 26px",
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: FT.paperWarm,
                borderBottom: `1px solid ${FT.navy08}`
              }}
            >
              <div style={{ flex: 1, fontSize: 12.5, color: FT.navy60 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    textTransform: "uppercase"
                  }}
                >
                  Or share your link
                </div>
                <div
                  style={{
                    fontSize: 13,
                    color: FT.navy,
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    marginTop: 2
                  }}
                >
                  {signupUrl}
                </div>
              </div>
              <button
                type="button"
                onClick={onCopy}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  fontSize: 12.5,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  border: `1px solid ${FT.navy15}`,
                  background: copied ? FT.mintBg : "#fff",
                  color: copied ? FT.mintDeep : FT.navy,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6
                }}
              >
                {copied ? (
                  <>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>{" "}
                    Copied
                  </>
                ) : (
                  "Copy link"
                )}
              </button>
            </div>

            <div style={{ padding: "22px 26px 8px", display: "grid", gap: 14 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14
                }}
              >
                <div>
                  <label style={labelStyle}>
                    Business name <span style={{ color: "#D04A4A" }}>*</span>
                  </label>
                  <input
                    value={form.name}
                    onChange={update("name")}
                    required
                    placeholder="e.g. Acme Plumbing Ltd"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>
                    Primary contact <span style={{ color: "#D04A4A" }}>*</span>
                  </label>
                  <input
                    value={form.contact}
                    onChange={update("contact")}
                    required
                    placeholder="First and last name"
                    style={inputStyle}
                  />
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr",
                  gap: 14
                }}
              >
                <div>
                  <label style={labelStyle}>
                    Email <span style={{ color: "#D04A4A" }}>*</span>
                  </label>
                  <input
                    value={form.email}
                    onChange={update("email")}
                    required
                    type="email"
                    placeholder="name@business.co.nz"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Phone</label>
                  <input
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="021 …"
                    style={inputStyle}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>
                  Financial documents{" "}
                  <span
                    style={{
                      color: FT.navy40,
                      fontWeight: 600,
                      textTransform: "none",
                      letterSpacing: 0
                    }}
                  >
                    (coming soon)
                  </span>
                </label>
                <div
                  aria-disabled="true"
                  style={{
                    cursor: "not-allowed",
                    border: `1.5px dashed ${FT.navy15}`,
                    background: FT.navy04,
                    borderRadius: 10,
                    padding: "18px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    opacity: 0.65
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "#fff",
                      border: `1px solid ${FT.navy08}`,
                      display: "grid",
                      placeItems: "center",
                      color: FT.navy40,
                      flex: "0 0 auto"
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 700,
                        color: FT.navy60
                      }}
                    >
                      Document upload coming soon
                    </div>
                    <div
                      style={{ fontSize: 11.5, color: FT.navy40, marginTop: 2 }}
                    >
                      You&apos;ll soon be able to attach bank statements,
                      P&amp;L and aged receivables here to speed up assessment.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label style={labelStyle}>Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={update("notes")}
                  rows={3}
                  placeholder="Anything useful for the FundTap team — deal context, timing, etc."
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: 70,
                    fontFamily: "inherit"
                  }}
                />
              </div>

              {error && (
                <div
                  style={{
                    fontSize: 13,
                    color: "#9B2C2C",
                    background: "#FBE6E6",
                    border: "1px solid #F5C2C2",
                    padding: "8px 12px",
                    borderRadius: 8
                  }}
                >
                  {error}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 26px 22px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderTop: `1px solid ${FT.navy08}`,
                background: "#fff",
                marginTop: 8,
                gap: 12,
                flexWrap: "wrap"
              }}
            >
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "10px 16px",
                  borderRadius: 8,
                  fontSize: 13.5,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  background: "#fff",
                  color: FT.navy,
                  border: `1px solid ${FT.navy15}`
                }}
              >
                Cancel
              </button>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => submit("link")}
                  disabled={!canSubmit}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 8,
                    fontSize: 13.5,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    background: "#fff",
                    color: canSubmit ? FT.navy : FT.navy40,
                    border: `1.5px solid ${canSubmit ? FT.navy : FT.navy15}`
                  }}
                >
                  {submitting ? "Sending…" : "Send sign-up link"}
                </button>
                <button
                  type="button"
                  onClick={() => submit("preassess")}
                  disabled={!canSubmit}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 8,
                    fontSize: 13.5,
                    fontWeight: 700,
                    fontFamily: "inherit",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    background: canSubmit ? FT.navy : FT.navy15,
                    color: canSubmit ? "#fff" : FT.navy60,
                    border: "none"
                  }}
                >
                  Send for pre-assessment
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

AddCustomerModal.propTypes = {
  signupUrl: PropTypes.string,
  copied: PropTypes.bool,
  onCopy: PropTypes.func,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func
};
